import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MessageCircle, Phone, MapPin, Clock, Send } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'wouter';
import { SEO } from '@/components/SEO';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <MarketingLayout>
      <SEO
        title="Contact | Prozpector"
        description="Get in touch with the Prozpector team. Contact us for sales inquiries, technical support, or partnership opportunities."
        url="https://prozpector.com/contact"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-32">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Have questions about Prozpector? We'd love to hear from you. Our team typically responds within 2 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold mb-2">Email Us</h3>
              <p className="text-sm text-gray-600 mb-3">General inquiries</p>
              <a href="mailto:hello@prozpector.com" className="text-sm font-medium text-black hover:underline">
                hello@prozpector.com
              </a>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold mb-2">Live Chat</h3>
              <p className="text-sm text-gray-600 mb-3">Instant support</p>
              <button className="text-sm font-medium text-black hover:underline">
                Start Chat →
              </button>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold mb-2">Sales</h3>
              <p className="text-sm text-gray-600 mb-3">Talk to our team</p>
              <a href="mailto:sales@prozpector.com" className="text-sm font-medium text-black hover:underline">
                sales@prozpector.com
              </a>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold mb-2">Support Hours</h3>
              <p className="text-sm text-gray-600 mb-3">Mon-Fri</p>
              <span className="text-sm font-medium text-black">
                9am - 6pm EST
              </span>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Form */}
              <div>
                <h2 className="text-3xl font-bold mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Your Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-2">
                      Subject *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your needs..."
                      className="w-full min-h-[150px]"
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full">
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                </form>
              </div>

              {/* Info */}
              <div>
                <h2 className="text-3xl font-bold mb-6">Quick Answers</h2>
                <div className="space-y-6">
                  <Card className="p-6">
                    <h3 className="font-bold mb-2">📞 Schedule a Demo</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Want to see Prozpector in action? Book a personalized demo with our team.
                    </p>
                    <Button variant="outline" className="w-full">
                      Book Demo Call
                    </Button>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-bold mb-2">💬 Live Chat Support</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Need immediate help? Our support team is available during business hours.
                    </p>
                    <Button variant="outline" className="w-full">
                      Start Chat
                    </Button>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-bold mb-2">📚 Help Center</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Browse our documentation, guides, and FAQs for quick answers.
                    </p>
                    <a href="/help">
                      <Button variant="outline" className="w-full">
                        Visit Help Center
                      </Button>
                    </a>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-bold mb-2">🚀 Enterprise Sales</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Need a custom plan for your team? Contact our enterprise sales team.
                    </p>
                    <a href="mailto:enterprise@prozpector.com">
                      <Button variant="outline" className="w-full">
                        Contact Enterprise
                      </Button>
                    </a>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Info */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Headquarters</h3>
                    <p className="text-gray-600">
                      San Francisco, CA<br />
                      United States
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  We're a remote-first company with team members across the globe, but our legal headquarters is in San Francisco.
                </p>
              </Card>

              <Card className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Department Contacts</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Support:</span>{' '}
                        <a href="mailto:support@prozpector.com" className="font-medium text-black hover:underline">
                          support@prozpector.com
                        </a>
                      </div>
                      <div>
                        <span className="text-gray-600">Sales:</span>{' '}
                        <a href="mailto:sales@prozpector.com" className="font-medium text-black hover:underline">
                          sales@prozpector.com
                        </a>
                      </div>
                      <div>
                        <span className="text-gray-600">Press:</span>{' '}
                        <a href="mailto:press@prozpector.com" className="font-medium text-black hover:underline">
                          press@prozpector.com
                        </a>
                      </div>
                      <div>
                        <span className="text-gray-600">Careers:</span>{' '}
                        <a href="mailto:careers@prozpector.com" className="font-medium text-black hover:underline">
                          careers@prozpector.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Before You Reach Out</h2>
            <p className="text-xl text-gray-600">Check if your question is already answered</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">What's your typical response time?</h3>
              <p className="text-gray-600">
                We respond to all inquiries within 2 hours during business hours (Mon-Fri, 9am-6pm EST). For urgent technical issues, our live chat offers immediate assistance.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">Do you offer phone support?</h3>
              <p className="text-gray-600">
                For security reasons, we don't offer phone support for technical issues. However, we're happy to schedule video calls for demos, onboarding, or enterprise discussions.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">Can I schedule a product demo?</h3>
              <p className="text-gray-600">
                Absolutely! Email sales@prozpector.com or use the contact form above to request a personalized demo. We'll show you exactly how Prozpector can automate your prospecting workflow.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">How do I report a bug or request a feature?</h3>
              <p className="text-gray-600">
                Email support@prozpector.com with details about the bug or feature request. We track all feedback in our product roadmap and prioritize based on customer needs.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof CTA */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Fill Your Calendar?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how we can start booking qualified meetings for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="bg-white text-black hover:bg-gray-100 border-white text-lg px-8 py-6">
                  Book a Discovery Call
                </Button>
              </a>
              <Link href="/features">
                <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 text-lg px-8 py-6">
                  See What We Deliver
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
